import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import api from 'common/api'
import features from 'common/college-features'
import SYSTEM_PERMISSIONS from 'common/system-permissions'
import store from 'store'
import { isFeatureEnabled } from 'store/college'
import { hasPermission, isShadow } from 'store/user'

/** --- Main Interface --- **/

export interface APIMetrics {
  sc_id: number
  senior_grad_year: number
  scoir_audience_total: number

  student_impressions_total: {
    impressions_total: number
    impressions_total_30_days_ago: number
  }

  reached_students_total: {
    reached_total: number
    reached_total_30_days_ago: number
  }

  engaged_students_totals: {
    engagement_all_stages: number
    engagement_all_stages_30_days_ago: number
  }

  registered_with_parent: ParentRegistrationStats
  engaged_registered_with_parent: ParentRegistrationStats

  attending_title_1_hs: Title1Stats
  engaged_attending_title_1_hs: Title1Stats

  first_gen_student: FirstGenStats
  engaged_first_gen_student: FirstGenStats

  engaged_academic_focus: {
    all_engaged_academic_focus: AcademicFocusItem[]
    following_academic_focus: AcademicFocusItem[]
  }

  reached_academic_focus: {
    audience_academic_focus: AcademicFocusItem[]
    reached_academic_focus: AcademicFocusItem[]
  }

  engaged_interest: {
    all_engaged_interests: InterestItem[]
    following_interests: InterestItem[]
  }

  reached_interest: {
    audience_interests: InterestItem[]
    reached_interests: InterestItem[]
    not_reached_interests: InterestItem[]
  }

  engaged_students_by_month: EngagedStudentsByMonth[]
  engagement_by_stage: EngagementByStage[]
  reached_by_grad_year: ReachedByGradYear[]

  competitors: Competitor[]
  competitors_adjusted: CompetitorAdjusted[]

  student_conversions: StudentConversion[]
}

/** --- Sub Interfaces --- **/

export interface ParentRegistrationStats {
  has_registered_parent_count_college: number
  has_registered_parent_pct_college: number
  has_registered_parent_count_scoir: number
  has_registered_parent_pct_scoir: number
}

export interface Title1Stats {
  is_title_1_count_college: number
  is_title_1_pct_college: number
  is_title_1_count_scoir: number
  is_title_1_pct_scoir: number
}

export interface FirstGenStats {
  is_first_gen_count_college: number
  is_first_gen_pct_college: number
  is_first_gen_count_scoir: number
  is_first_gen_pct_scoir: number
}

export interface AcademicFocusItem {
  name: string
  count: number
  message_count: number | null
  content_item_count: number | null
}

export interface InterestItem {
  name: string
  count: number
  message_count: number | null
  content_item_count: number | null
}

export interface EngagedStudentsByMonth {
  grad_year: string | number
  by_month: {
    month: number
    month_name: string
    month_serial: number
    year: number
    by_stage: {
      stage: string
      count: number
    }[]
  }[]
}

export interface EngagementByStage {
  stage: string
  num_students: number
}

export interface ReachedByGradYear {
  grad_year: number
  counts: {
    college_reached_count: number
    scoir_audience_count: number
  }
}

export interface Competitor {
  name: string
  sc_id: number
  total_prospect_count: number
  mutual_prospect_count: number
}

export interface CompetitorAdjusted {
  sc_id: number
  name: string
  total_prospect_count: number
  adjusted_prospect_count: number
  mutual_prospect_count: number
}

export interface StudentConversion {
  graduation_year: number
  interested: number
  applications: number
  accepted: number
  enrolled: number
}

type MetricsState = {
  record?: APIMetrics
  error?: string
  loading: boolean
}

const INITIAL_STATE: MetricsState = {
  loading: true,
}

type SliceState = {
  metrics: MetricsState
}

export const selectReachedInterest = (store: SliceState) =>
  store.metrics.record?.reached_interest?.reached_interests || []
export const selectReachedAcademicFocus = (store: SliceState) =>
  store.metrics.record?.reached_academic_focus?.reached_academic_focus || []
export const showCollegeMetrics = () => {
  const state = store.getState()
  const supportsAnalytics = isFeatureEnabled(state, features.SUPPORTS_STUDENT_ANALYTICS as keyof typeof features)
  const canUserSeeMetrics = hasPermission(state, SYSTEM_PERMISSIONS.VIEW_COLLEGE_ENGAGEMENT_METRICS)
  const isShadowUser = isShadow(state)
  return (supportsAnalytics && canUserSeeMetrics) || isShadowUser
}

export const fetchMetrics = createAsyncThunk('metrics/fetch', async (scid: number) => {
  if (!showCollegeMetrics()) {
    return
  }
  return api.get<APIMetrics>(`/api/colleges/${scid}/metrics`)
})

const slice = createSlice({
  name: 'metrics',
  initialState: INITIAL_STATE,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchMetrics.fulfilled, (state, action) => {
      state.record = action.payload
      state.loading = false
    })
    builder.addCase(fetchMetrics.rejected, (state, action) => {
      state.error = action.error.message || 'An error occurred while fetching metrics.'
      state.loading = false
    })
  },
})

export default slice.reducer

export const actions = {
  ...slice.actions,
  fetchMetrics,
}
